import { membershipsApi } from '../../api/memberships.api.js';
import { ConfirmationMark } from '../../components/common/ConfirmationMark.js';
import { pushConversionOnce } from '../../utils/conversion-tracking.js';
import { translate } from '../../i18n/index.js';
import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useBookingStore } from '../../store/booking.store.js';
import { useMembershipCheckoutStore } from '../../store/membership-checkout.store.js';

export function MembershipConfirm() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [purchaseConfirmed, setPurchaseConfirmed] = useState(false);
  const purchaseId = searchParams.get('purchaseId');
  useEffect(() => {
    if (!purchaseId) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout>;
    async function check() {
      try {
        const { data } = await membershipsApi.getPurchase(purchaseId!);
        if (cancelled) return;
        if (data.status === 'SUCCEEDED') setPurchaseConfirmed(true);
        if (data.status === 'SUCCEEDED' && data.plan.tier !== 'BASIC') {
          pushConversionOnce({ event: 'membership_purchased', order_id: data.id,
            price: data.amountInSmallestUnit / 100, currency: data.currency,
            payment_status: 'paid', product: data.plan.tier === 'ELITE' ? 'elite' : 'premium',
          }, data.id);
        } else if (data.status === 'PENDING') timer = setTimeout(() => void check(), 1000);
      } catch { /* A failed read must not produce a conversion. */ }
    }
    void check();
    return () => { cancelled = true; clearTimeout(timer); };
  }, [purchaseId]);
  const reset = useMembershipCheckoutStore((state) => state.reset);
  const returnTo = useMembershipCheckoutStore((state) => state.selection?.returnTo);
  const hasPendingBookingSelection = useBookingStore((state) =>
    Boolean(state.gymId && (state.selectedSlots.length > 0 || state.selectedSlot)),
  );
  const shouldReturnToCheckoutRef = useRef(
    searchParams.get('returnTo') === 'checkout' || returnTo === 'checkout',
  );

  useEffect(() => {
    if (shouldReturnToCheckoutRef.current) {
      if (purchaseId && !purchaseConfirmed) return;
      const timeout = window.setTimeout(() => {
        reset();
        void navigate('/checkout', { replace: true });
      }, 1200);

      return () => window.clearTimeout(timeout);
    }

    reset();
    return undefined;
  }, [navigate, reset, purchaseId, purchaseConfirmed]);

  return (
    <main className="af-screen af-booking-confirm-screen">
      <section
        className="af-shell af-booking-confirm-shell"
        aria-labelledby="membership-confirm-title"
      >
        <ConfirmationMark />

        <h1 id="membership-confirm-title" className="af-confirm-title">
          {translate(
            'pages.public.membership_confirm.membership_confirm.h1_confirm_title.membership_confirmation',
          )}
        </h1>

        {shouldReturnToCheckoutRef.current && (
          <p
            style={{
              margin: '-22px 0 28px',
              color: 'var(--af-muted)',
              fontSize: 14,
              fontWeight: 700,
            }}
          >
            {translate('pages.public.membership_confirm.membership_confirm.p.booking_payment_back')}
          </p>
        )}

        {!shouldReturnToCheckoutRef.current && (
          <>
            <div className="af-confirm-actions">
              {hasPendingBookingSelection && (
                <Link className="af-outline-btn" to="/checkout" style={{ textDecoration: 'none' }}>
                  <DumbbellIcon />
                  {translate(
                    'pages.public.membership_confirm.membership_confirm.link_outline_btn.booking',
                  )}
                </Link>
              )}
              <Link className="af-outline-btn" to="/membership" style={{ textDecoration: 'none' }}>
                <MembershipIcon />
                {translate(
                  'pages.public.membership_confirm.membership_confirm.link_outline_btn.membership',
                )}
              </Link>
              <Link className="af-outline-btn" to="/gyms" style={{ textDecoration: 'none' }}>
                <DumbbellIcon />
                {translate(
                  'pages.public.membership_confirm.membership_confirm.link_outline_btn.book',
                )}
              </Link>
            </div>

            <Link className="af-confirm-home-link" to="/">
              {translate(
                'pages.public.membership_confirm.membership_confirm.link_confirm_home_link.back',
              )}
            </Link>
          </>
        )}
      </section>
    </main>
  );
}

function MembershipIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <rect x="2" y="3" width="12" height="10" rx="2" stroke="currentColor" strokeWidth="1.4" />
      <path d="M5 7h6M5 10h4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  );
}

function DumbbellIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path d="M5.5 8h5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      <path d="M3 5.5v5M13 5.5v5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      <path
        d="M1.5 6.5v3M14.5 6.5v3"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}
